import { useState } from 'react';
import { congeVersDto, dtoVersConge } from '../../api/mappers';
import { congesApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { joursOuvres } from '../../lib/dates';
import { uid } from '../../lib/utils';
import { C } from '../../theme/palette';

/** Absences qui se justifient par un certificat médical. */
const AVEC_CERTIFICAT = ['maladie', 'accident'];

const VIDE = { debut: '', fin: '', jours: 1, type: 'vacances', certificat: null };

const fd = (iso) => {
  if (!iso) return '';
  const z = ('' + iso).split('-');
  return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
};

/**
 * Onglet « Congés » de l'espace employé : demander une absence, suivre ses
 * demandes et répondre à la date que la direction propose.
 *
 * Pour une maladie ou un accident, le certificat médical se joint à la
 * demande ; il part au serveur avec elle et ne se lit ensuite que par
 * l'employé et la direction.
 *
 * @param {object} props propriétés de l'onglet
 * @param {object} props.emp fiche de l'employé connecté
 * @param {object[]} props.conges demandes de congé
 * @param {(fn: (p: object[]) => object[]) => void} props.setConges mise à jour des demandes
 * @param {(texte: string) => void} [props.setFlash] message bref
 * @returns {import('react').ReactElement} l'onglet
 */
/**
 * Congés de l'employé connecté : demande, suivi, réponse à une autre date.
 *
 * @param {object} props propriétés
 * @param {object} props.emp fiche de l'employé
 * @param {Array} props.conges congés de la société
 * @param {Function} props.setConges mise à jour des congés
 * @param {Function} [props.setFlash] message de confirmation
 * @param {boolean} [props.validesDOffice] vrai pour le patron sur sa propre
 *        fiche : l'approbateur, c'est lui, sa demande est acquise d'avance
 * @returns {import('react').ReactElement} l'écran
 */
export function MesConges({
  emp,
  conges,
  setConges,
  setFlash,
  validesDOffice = false,
  absence = false,
}) {
  // « Maladie / Accident » : l'écran s'ouvre sur la maladie, ne propose que
  // ces deux motifs et met le certificat en avant. L'ouvrier ne trouvait pas
  // où l'envoyer, rangé au fond de la liste des vacances.
  const vide = absence ? { ...VIDE, type: 'maladie' } : VIDE;
  const [vac, setVac] = useState(vide);
  const [envoi, setEnvoi] = useState(false);
  const flash = (t) => setFlash && setFlash(t);
  const avecCertificat = AVEC_CERTIFICAT.includes(vac.type);
  // Le serveur approuve d'office le conge du patron sur sa fiche : l'ecran le
  // dit tout de suite plutot que d'annoncer une validation qui n'attend personne.
  const confirmation = validesDOffice
    ? '✓ Congé enregistré et validé — l’approbateur, c’est toi'
    : '✓ Demande envoyée — la direction va la valider';

  const envoie = async () => {
    if (vac.fin && vac.debut && vac.fin < vac.debut) {
      flash('⚠ La date de fin doit être après la date de début');
      return;
    }
    const demande = {
      id: uid(),
      empId: emp.id,
      debut: vac.debut,
      fin: vac.fin,
      type: vac.type || 'vacances',
      jours: vac.jours,
      statut: validesDOffice ? 'Approuvé' : 'En attente',
    };
    if (!(avecCertificat && vac.certificat)) {
      setConges((p) => [...p, demande]);
      setVac(vide);
      flash(confirmation);
      return;
    }
    // Le certificat s'attache a une demande enregistree : celle-ci part donc
    // tout de suite, et le fichier la suit.
    setEnvoi(true);
    let creee = null;
    try {
      creee = await congesApi.cree(congeVersDto(demande));
      // La demande existe désormais au serveur : elle s'affiche tout de suite.
      // Si le fichier échoue ensuite, un nouvel essai ne la recrée pas — la
      // direction recevait sinon deux demandes pour la même absence.
      setConges((p) => [...p, dtoVersConge(creee)]);
      const avecFichier = await congesApi.joindreCertificat(
        creee.id,
        vac.certificat,
        vac.certificat.name,
      );
      setConges((p) => p.map((x) => (x.id === creee.id ? dtoVersConge(avecFichier) : x)));
      setVac(vide);
      flash(confirmation);
    } catch (e) {
      if (creee) {
        setVac(vide);
        flash(
          '⚠ Ta demande est envoyée, mais le certificat n’a pas pu être joint (' +
            (e?.message || 'erreur') +
            '). Joins-le depuis ta liste ci-dessous.',
        );
      } else {
        flash('⚠ ' + (e?.message || 'La demande n’a pas pu être envoyée.'));
      }
    } finally {
      setEnvoi(false);
    }
  };

  // Joindre le certificat à une absence déjà envoyée : il arrive souvent le
  // lendemain, chez le médecin.
  const joindreApres = async (c, fichier) => {
    if (!fichier || !Number.isInteger(c.id)) return;
    try {
      const dto = await congesApi.joindreCertificat(c.id, fichier, fichier.name);
      setConges((p) => p.map((x) => (x.id === c.id ? dtoVersConge(dto) : x)));
      flash('✓ Certificat joint — la direction le voit.');
    } catch (e) {
      flash('⚠ ' + (e?.message || 'Le certificat n’a pas pu être joint.'));
    }
  };

  const repond = async (c, accepte) => {
    try {
      const dto = await congesApi.repond(c.id, accepte);
      setConges((p) => p.map((x) => (x.id === c.id ? dtoVersConge(dto) : x)));
      flash(accepte ? '✓ Nouvelle date acceptée' : 'Date refusée — reparles-en à la direction');
    } catch (e) {
      flash('⚠ ' + (e?.message || 'La réponse n’a pas pu être envoyée.'));
    }
  };

  const mine = (conges || []).filter((c) => c.empId === emp.id);
  const jPris = mine.filter((c) => c.statut === 'Approuvé').reduce((a, c) => a + (c.jours || 0), 0);
  const jAtt = mine
    .filter((c) => c.statut === 'En attente')
    .reduce((a, c) => a + (c.jours || 0), 0);

  return (
    <div>
      <Card style={{ marginBottom: 11 }}>
        {absence ? (
          <Bar
            icon="🤒"
            title="Maladie / accident"
            color={C.red}
            sub="Prends ton certificat en photo : il part directement à la direction"
          />
        ) : (
          <Bar
            icon="🌴"
            title={
              validesDOffice ? 'Poser un congé / une absence' : 'Demander un congé / une absence'
            }
            color={C.teal}
            sub={
              validesDOffice
                ? 'Validé d’office : tu es l’approbateur'
                : 'Ta demande part à la direction pour validation'
            }
          />
        )}
        <div style={{ padding: 13 }}>
          <Inp label="Motif">
            <select
              aria-label="Motif"
              value={vac.type || 'vacances'}
              onChange={(e) => setVac((p) => ({ ...p, type: e.target.value }))}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '10px',
                fontSize: 11,
                background: '#fff',
              }}
            >
              {!absence && <option value="vacances">Vacances</option>}
              {!absence && <option value="congé">Congé</option>}
              <option value="maladie">Maladie</option>
              <option value="accident">Accident</option>
            </select>
          </Inp>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            <Inp
              label="Du"
              type="date"
              value={vac.debut}
              onChange={(e) =>
                setVac((p) => {
                  const n = joursOuvres(e.target.value, p.fin);
                  return { ...p, debut: e.target.value, jours: n ?? p.jours };
                })
              }
            />
            <Inp
              label="Au"
              type="date"
              value={vac.fin}
              onChange={(e) =>
                setVac((p) => {
                  // Le nombre de jours se déduit des dates : « 1 jour » restait
                  // affiché pour une absence du lundi au mercredi.
                  const n = joursOuvres(p.debut, e.target.value);
                  return { ...p, fin: e.target.value, jours: n ?? p.jours };
                })
              }
            />
          </div>
          <Inp
            label="Nombre de jours ouvrés"
            type="number"
            value={vac.jours}
            onChange={(e) => setVac((p) => ({ ...p, jours: +e.target.value }))}
          />
          {avecCertificat && (
            <label
              style={{
                display: 'block',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 800,
                color: vac.certificat ? C.green : C.teal,
                border: '1px solid ' + (vac.certificat ? C.green : C.teal),
                borderRadius: 9,
                padding: '9px',
                textAlign: 'center',
                margin: '9px 0 2px',
              }}
            >
              {vac.certificat
                ? '📎 Certificat joint ✓ — ' + (vac.certificat.name || 'photo')
                : '📷 Prendre en photo le certificat médical (ou choisir un PDF)'}
              <input
                type="file"
                accept="image/*,application/pdf"
                aria-label="Certificat médical"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const fichier = e.target.files && e.target.files[0];
                  if (fichier) setVac((p) => ({ ...p, certificat: fichier }));
                  e.target.value = '';
                }}
              />
            </label>
          )}
          <div style={{ height: 8 }} />
          <Btn
            label={
              envoi
                ? 'Envoi en cours…'
                : absence
                  ? '📤 Envoyer à la direction'
                  : '📤 Envoyer ma demande'
            }
            color={C.teal}
            full
            disabled={!vac.debut || !vac.fin || envoi}
            onClick={envoie}
          />
          <div
            style={{
              fontSize: 8.5,
              color: C.muted,
              marginTop: 8,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            {validesDOffice
              ? 'Ton congé compte aussitôt dans tes jours approuvés, et le bureau le voit sur ta fiche.'
              : 'Tu seras informé ici dès que le patron ou le technicien aura répondu.'}
          </div>
        </div>
      </Card>
      {!mine.length ? (
        <div
          style={{
            textAlign: 'center',
            padding: '24px 12px',
            color: C.muted,
            fontSize: 10,
            lineHeight: 1.5,
          }}
        >
          Tu n&apos;as pas encore fait de demande.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 11 }}>
            <div
              style={{
                flex: 1,
                background: C.greenL,
                borderRadius: 9,
                padding: '9px 11px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 8,
                  color: C.green,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                Jours approuvés
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.green }}>{jPris}</div>
            </div>
            <div
              style={{
                flex: 1,
                background: '#FEF6E7',
                borderRadius: 9,
                padding: '9px 11px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 8,
                  color: C.amber,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                En attente
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.amber }}>{jAtt}</div>
            </div>
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              color: C.muted,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Historique de mes demandes
          </div>
          {mine
            .slice()
            .reverse()
            .map((c) => {
              const prop = c.statut === 'Date proposée';
              const sc =
                c.statut === 'Approuvé'
                  ? C.green
                  : c.statut === 'Refusé'
                    ? C.red
                    : prop
                      ? C.navy
                      : C.amber;
              const si =
                c.statut === 'Approuvé' ? '✓' : c.statut === 'Refusé' ? '✕' : prop ? '📅' : '⏳';
              return (
                <div
                  key={c.id}
                  style={{
                    background: C.white,
                    border: '1px solid ' + C.border,
                    borderLeft: '3px solid ' + sc,
                    borderRadius: 10,
                    padding: '11px 13px',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: sc + '18',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 15,
                        color: sc,
                        flexShrink: 0,
                      }}
                    >
                      {si}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.ink,
                          textTransform: 'capitalize',
                        }}
                      >
                        {c.type || 'congé'} · {fd(c.debut)}
                        {c.fin && c.fin !== c.debut ? ' → ' + fd(c.fin) : ''}
                      </div>
                      <div style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>
                        {c.jours || 1} jour{(c.jours || 1) > 1 ? 's' : ''}
                        {c.certificat ? ' · 📎 certificat joint' : ''}
                      </div>
                      {!c.certificat &&
                        AVEC_CERTIFICAT.includes(c.type) &&
                        Number.isInteger(c.id) && (
                          <label
                            style={{
                              display: 'inline-block',
                              marginTop: 5,
                              fontSize: 9.5,
                              fontWeight: 800,
                              color: C.teal,
                              cursor: 'pointer',
                            }}
                          >
                            📎 Joindre le certificat
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              aria-label={'Certificat pour l’absence du ' + fd(c.debut)}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const fichier = e.target.files && e.target.files[0];
                                e.target.value = '';
                                joindreApres(c, fichier);
                              }}
                            />
                          </label>
                        )}
                    </div>
                    <Pill label={c.statut} color={sc} size={8} />
                  </div>
                  {prop && (
                    <div
                      style={{
                        marginTop: 9,
                        background: C.navy + '0c',
                        border: '1px solid ' + C.navy + '33',
                        borderRadius: 9,
                        padding: '10px 11px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.navy,
                          marginBottom: 8,
                          lineHeight: 1.5,
                        }}
                      >
                        📅 La direction te propose plutôt :{' '}
                        <b>
                          {fd(c.debutPropose)}
                          {c.finPropose && c.finPropose !== c.debutPropose
                            ? ' → ' + fd(c.finPropose)
                            : ''}
                        </b>
                      </div>
                      <div style={{ display: 'flex', gap: 7 }}>
                        <Btn
                          label="✓ Accepter"
                          sm
                          color={C.green}
                          full
                          onClick={() => repond(c, true)}
                        />
                        <Btn
                          label="✕ Refuser"
                          sm
                          ghost
                          danger
                          color={C.red}
                          full
                          onClick={() => repond(c, false)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </>
      )}
    </div>
  );
}
